import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { Context } from '..';
import SurveyFormModal from './SurveyFormModal';
import SurveyStatistics from './SurveyStatistics';

const Surveys = () => {
  const { user: userStore } = useContext(Context);
  const user = userStore.user;
  const [surveys, setSurveys] = useState([]);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openStatsModal, setOpenStatsModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/web/surveys?clinicId=${user.clinicId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      setSurveys(data);
    } catch (err) {
      setError('Ошибка загрузки опросов');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, [user.clinicId]);

  const handleCreateSurvey = () => {
    setSelectedSurvey(null);
    setOpenFormModal(true);
  };

  const handleEditSurvey = (survey) => {
    if (survey.creatorId !== user.id) {
      setError('Только создатель может редактировать опрос');
      return;
    }
    setSelectedSurvey(survey);
    setOpenFormModal(true);
  };

  const handleDeleteSurvey = async (survey) => {
    if (survey.creatorId !== user.id) {
      setError('Только создатель может удалить опрос');
      return;
    }

    if (!window.confirm('Вы уверены, что хотите удалить этот опрос?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/web/surveys/${survey.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Ошибка при удалении опроса');
      }

      fetchSurveys();
    } catch (err) {
      setError('Ошибка при удалении опроса');
      console.error(err);
    }
  };

  const handleViewStatistics = (survey) => {
    setSelectedSurvey(survey);
    setOpenStatsModal(true);
  };

  const handleCloseFormModal = (shouldRefresh) => {
    setOpenFormModal(false);
    setSelectedSurvey(null);
    if (shouldRefresh) {
      fetchSurveys();
    }
  };

  const handleCloseStatsModal = () => {
    setOpenStatsModal(false);
    setSelectedSurvey(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Опросы</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateSurvey}
        >
          Создать опрос
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Вопросов</TableCell>
              <TableCell>Создатель</TableCell>
              <TableCell>Создан</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surveys.map((survey) => (
              <TableRow key={survey.id}>
                <TableCell>{survey.title}</TableCell>
                <TableCell>
                  {survey.description
                    ? survey.description.substring(0, 50) + '...'
                    : '-'}
                </TableCell>
                <TableCell>{survey.questions?.length || 0}</TableCell>
                <TableCell>{survey.creator?.name || 'N/A'}</TableCell>
                <TableCell>
                  {new Date(survey.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleViewStatistics(survey)}
                    title="Статистика"
                  >
                    <BarChartIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditSurvey(survey)}
                    disabled={survey.creatorId !== user.id}
                    title="Редактировать"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteSurvey(survey)}
                    disabled={survey.creatorId !== user.id}
                    title="Удалить"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {surveys.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Нет доступных опросов
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SurveyFormModal
        open={openFormModal}
        onClose={handleCloseFormModal}
        survey={selectedSurvey}
        clinicId={user.clinicId}
        creatorId={user.id}
      />

      <SurveyStatistics
        open={openStatsModal}
        onClose={handleCloseStatsModal}
        survey={selectedSurvey}
      />
    </Box>
  );
};

export default Surveys;
